import { useMutation, useQuery } from "react-query"; // TanStack Query
import { useAuth } from "../../../context";
import { Issb } from "../../../api";
import { useConfirmedMutation } from "../../../context/Mutation/useConfirmedMutation";
import assert from "utils/assert";

export const issbKeys = {
  questionnaireRound: (id) => id ? ["questionnaireRound", id] : ["questionnaireRound"],
  questionList: () => ["questionList"],
  auditQuestionList: () => ["auditQuestionList"],
  roundStages: () => ["roundStages"],
  entity: () => ["entity"],
  industry: () => ["industry"],
  actions: () => ["actions"],
  audit: () => ["audit"],
  configuration: () => ["issbConfiguration"],
  metrics: () => ["issbMetrics"],
  activities: () => ["issbActivities"],
  answer: () => ["answer"],
  dashboard: () => ["issbDashboard"],
  comments: () => ["comments"],
};

export function useGetQuestionnaireRounds(paramsString, config) {
  const { callApi } = useAuth();
  return useQuery(
    [...issbKeys.questionnaireRound(), paramsString],
    () => callApi(Issb.getQuestionnaireRounds, paramsString),
    config
  );
}
/**
 *   // Maybe refactor the API to use a single list of questions, with a filterBy string?? It'd be more generic
 // without the groupName wrappers

 * Call Issb.getQuestionsOfRound()
 * @param {string?} paramsString url-encoded params?? TODO document this. Often null
 * @param {!string} id q-round id
 * @param {UseQueryOptions} config e.g. enabled
 * @returns {UseQueryResult} 
 */
export function useGetQuestionsOfRound(paramsString, id, config) {
  const { callApi } = useAuth();
  return useQuery(
    [...issbKeys.questionList(), paramsString, id],
    () => callApi(Issb.getQuestionsOfRound, { id, paramsString }),
    config
  );
}

export function useGetQuestionsOfAudit(paramsString, config) {
  const { callApi } = useAuth();
  return useQuery(
    [...issbKeys.auditQuestionList(), paramsString],
    () => callApi(Issb.getAuditQuestionsOfRound, { paramsString }),
    config
  );
}

export function useGetStagesOfRound(id, config) {
  assert(id || config.enabled === false, "id is required for get-stages-of-round");
  const { callApi } = useAuth();
  return useQuery([...issbKeys.roundStages(), id], () => callApi(Issb.getStagesOfRound, { id }), config);
}

export function useSaveAnswer(config) {
  const { callApi } = useAuth();
  return useConfirmedMutation((data) => callApi(Issb.saveAnswer, data), config);
}

/**
 * Use this hook to exclude/restore an answer from the questionnaire.
 * @param config
 * @returns {UseMutationResult<*, unknown, void, unknown>}
 */
export function useExcludeAnswer(config) {
  const { callApi } = useAuth();
  return useConfirmedMutation((data) => callApi(Issb.excludeAnswer, data), config);
}

export function useGetOrganizationEntities(config) {
  const { callApi } = useAuth();
  return useQuery(issbKeys.entity(), () => callApi(Issb.getOrganizationEntities), config);
}

export function useGetEntitiesForUser(config) {
  const { callApi } = useAuth();
  return useQuery("getEntitiesForUser", () => callApi(Issb.getEntitiesForUser), config);
}

export function useGetOrganizationIndustries(config) {
  const { callApi } = useAuth();
  return useQuery(issbKeys.industry(), () => callApi(Issb.getOrganizationIndustries), config);
}

export function useDeleteEntity(config) {
  const { callApi } = useAuth();
  return useConfirmedMutation((id) => callApi(Issb.deleteEntity, id), config);
}

export function useStartAudit(config) {
  const { callApi } = useAuth();
  return useConfirmedMutation((data) => callApi(Issb.saveAuditOfRound, data), config);
}

export function useGetActionsOfAnswer(id, config) {
  const { callApi } = useAuth();
  return useQuery([...issbKeys.actions(), id], () => callApi(Issb.getActionsOfAnswer, { id }), config);
}

export function useSubmitAction(config) {
  const { callApi } = useAuth();
  return useConfirmedMutation((data) => callApi(Issb.submitAction, data), config);
}

export function useGetActiveAudit(id, config) {
  const { callApi } = useAuth();
  return useQuery([...issbKeys.audit(), id], () => callApi(Issb.getActiveAudit, { id }), config);
}

export function useGetAllAudit(questionnaireId, config) {
  const { callApi } = useAuth();
  return useQuery([...issbKeys.audit(), questionnaireId], () => callApi(Issb.getAllAudit, { questionnaireId }), config);
}

export function useCloseAudit(config) {
  const { callApi } = useAuth();
  return useConfirmedMutation((data) => callApi(Issb.closeAudit, data), config);
}

export function useChangeOwner(config) {
  const { callApi } = useAuth();
  return useConfirmedMutation((data) => callApi(Issb.changeOwner, data), config);
}

export function useChangeAssignee(config) {
  const { callApi } = useAuth();
  return useConfirmedMutation((data) => callApi(Issb.changeAssignee, data), config);
}

export function useGetIssbConfiguration(config) {
  return useQuery(issbKeys.configuration(), () => Issb.getConfiguration(), config);
}

export function useCreatePlan(config) {
  return useMutation((data) => Issb.createPlan(data), config);
}

export function useSendPlan(config) {
  return useMutation((surveyId) => Issb.sendPlan(surveyId), config);
}

export function useGetIssbMetrics(subIndustryId, config) {
  return useQuery([...issbKeys.metrics(), subIndustryId], () => Issb.getMetrics(subIndustryId), config);
}

export function useGetActivities(id, config) {
  const { callApi } = useAuth();
  return useQuery([...issbKeys.activities(), id], () => callApi(Issb.getActivities, { id }), config);
}

export function useGetAnswerOfQuestion(id, config) {
  const { callApi } = useAuth();
  return useQuery([...issbKeys.answer()], () => callApi(Issb.getQuestion, { id }), config);
}

export function useGetDashboardData(id, config) {
  const { callApi } = useAuth();
  return useQuery([...issbKeys.dashboard()], () => callApi(Issb.getDashboardData, { id }), config);
}

// export function useGenerateDisclosureReport(id, config) {
//   const { callApi } = useAuth();
//   return useQuery([...issbKeys.answer()], () => callApi(Issb.downloadDisclosure, { id }), config);
// }

export function useGenerateDisclosureReport(config) {
  const { callApi } = useAuth();
  return useConfirmedMutation((data) => callApi(Issb.downloadDisclosure, data), config, {
    title: "Download Disclosure Report",
    message: "Are you sure you want to download the disclosure report?",
    confirmText: "Download",
    cancelText: "Cancel",
  });
}

export function useUpdateEntityUsers(config) {
  const { callApi } = useAuth();
  return useConfirmedMutation((data) => callApi(Issb.updateEntityUsers, data), config);
}

export function useInitFoundationRound(config) {
  const { callApi } = useAuth();
  return useMutation(() => callApi(Issb.initFoundationRound), config);
}

export function useAddYearToFoundationRound(config) {
  const { callApi } = useAuth();
  return useMutation((year) => callApi(Issb.addYearToFoundationRound, year), config);
}
